// apps/ers/src/app/modules/ers-leave-management/components/request-leave-page/request-leave-page.component.ts

import { Component, OnInit, DestroyRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ApiNotificationService } from '@rcs/notify';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SuccessDialogComponent } from '../../dialogs/success-dialog/success-dialog';
import { LeaveHolidayPanelComponent } from '../leave-holiday-panel/leave-holiday-panel.component';
import { formatDateLong, getDayName, formatDateMDY, parseLocalDate, extractDateString } from '../../utils/date.utils';
import { LeaveType, LeaveBalance, WorkedDate, TakenCompOff, Manager } from '../../models/leave.model';
import { forkJoin } from 'rxjs';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { dateFilterParamsLong,
  filterAndSortOptions,
} from '@rcs/utils';

import { LeaveService } from '../../services/leave.service';
import { LmLoadingOverlayComponent } from '../lm-loading-overlay/lm-loading-overlay.component';
import { DocPreviewDialogComponent, DocPreviewDialogData } from '@rcs/doc-viewer';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSearchableSelectComponent } from '@rcs/ers-elementals';

@Component({
  selector: 'ers-request-leave-page',
  standalone: true,
  imports:[
    TranslocoPipe,
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    LeaveHolidayPanelComponent,
    SuccessDialogComponent,
    LmLoadingOverlayComponent,
    ErsSearchableSelectComponent
  ],
  templateUrl: './request-leave-page.component.html',
  styleUrls: ['./request-leave-page.component.scss'],
})
export class RequestLeavePageComponent implements OnInit {
  // Tab state
  leftTab = 0; // 0: Leave Request | 1: Comp Off Request
  rightTab = 0; // 0: Company Holiday | 1: Optional Holiday | 2: Compoff

  // Forms
  leaveForm: FormGroup;
  compOffForm: FormGroup;

  // Date picker controls for multi-date selection
  compOffDateCtrl = new FormControl<Date | null>(null);
  workedDateCtrl = new FormControl<Date | null>(null);

  // Leave-type autocomplete
  @ViewChild('leaveTypeInput', { read: MatAutocompleteTrigger })
  leaveTypeAutoTrigger!: MatAutocompleteTrigger;

  leaveTypeSearchCtrl = new FormControl('');

  get filteredLeaveTypes(): LeaveType[] {
    return filterAndSortOptions(
      this.leaveTypes,
      this.leaveTypeSearchCtrl.value ?? '',
      (t) => t.name
    );
  }

  // Today — used as upper bound for worked dates
  today = new Date();

  // Chip selections
  selectedLeaveManager: Manager | null = null;
  selectedCompOffManagers: Manager[] = [];
  selectedCompOffDates: Date[] = [];
  selectedWorkedDates: Date[] = [];

  // Data
  leaveTypes: LeaveType[] = [];
  managers: Manager[] = [];
  workedDates: WorkedDate[] = [];
  takenCompOffs: TakenCompOff[] = [];
  leaveBalances: LeaveBalance[] = [];
  policyDocuments: any[] = [];
  policyLinkUrl: string | null = null;
  policyLinkName: string | null = null;
  policyLinks: { url: string; name: string }[] = [];

  get selectedLeaveTypeName(): string {
    const id = this.leaveForm.getRawValue().leaveTypeId;
    return this.leaveTypes.find(t => t.id === id)?.name ?? '';
  }

  get selectedLeaveBalance(): number {
    const id = (this.leaveForm.getRawValue().leaveTypeId ?? '').toLowerCase();
    const bal = this.leaveBalances.find(b => (b.leaveTypeId ?? '').toLowerCase() === id);
    return bal?.availableBalance ?? 0;
  }

  get descriptionLength(): number {
    return (this.leaveForm.get('description')?.value ?? '').length;
  }

  get compOffBalance(): number {
    const fromBalance = this.leaveBalances.find(b =>
      b.leaveTypeName?.toLowerCase().includes('comp')
    );
    if (fromBalance) return fromBalance.availableBalance;
    return this.workedDates.filter(d => d.status === 'Available').length;
  }

  // Right panel grid data
  companyHolidayRows: any[] = [];
  optionalHolidayRows: any[] = [];
  pendingWorkedRows: any[] = [];

  // Column defs
  holidayColDefs: ColDef[] = [];
  workedDateColDefs: ColDef[] = [];

  isLoading = false;
  isSubmitting = false;

  private destroyRef = inject(DestroyRef);
  private notify = inject(ApiNotificationService);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private leaveService: LeaveService,
    private dialog: MatDialog,
  ) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.leaveForm = this.fb.group({
      requestToId: [''],
      leaveTypeId: ['', Validators.required],
      startDate: [today, Validators.required],
      endDate: [null, Validators.required],
      description: ['', Validators.maxLength(2000)],
    });

    this.compOffForm = this.fb.group({
      requestedDays: [1, [Validators.required, Validators.min(1)]],
      requestToIds: [[]],
    });
  }

  ngOnInit(): void {
    this.setupColumnDefs();
    this.loadData();
  }



  // ─── Column Definitions ───────────────────────────────────────────────────

  setupColumnDefs(): void {
    this.holidayColDefs = [
      {
        headerName: 'Holiday Name',
        field: 'holidayName',
        flex: 1,
        minWidth: 160,
        filter: 'agSetColumnFilter',
        cellRenderer: (params: ICellRendererParams) => {
          const star = params.data?.isMandatory
            ? `<span style="color:#FFC107;margin-right:5px;font-size:14px;">★</span>`
            : `<span style="display:inline-block;width:19px;"></span>`;
          return `<span style="display:flex;align-items:center;">${star}${params.value ?? ''}</span>`;
        },
      },
      { headerName: 'Date', field: 'date', width: 110, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong },
      { headerName: 'Day', field: 'day', width: 110, filter: 'agSetColumnFilter' },
      { headerName: 'Type', field: 'type', width: 100, filter: 'agSetColumnFilter' },
    ];

    this.workedDateColDefs = [
      {
        headerName: 'Date of Worked', field: 'dateWorked', flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong,
        sort: 'desc', sortIndex: 0,
        comparator: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      },
      { headerName: 'Requested Date', field: 'requestedDate', flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong },
      { headerName: 'Status', field: 'status', width: 100, filter: 'agSetColumnFilter' },
    ];
  }

  // ─── Data Loading ────────────────────────────────────────────────────────

  loadData(): void {
    this.isLoading = true;
    let pending = 7; // number of parallel API calls
    const done = () => { if (--pending === 0) this.isLoading = false; };

    this.leaveService
      .getLeaveTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((types) => {
        done();
        this.leaveTypes = types;
        const preselect = this.route.snapshot.queryParams['leaveTypeId'];
        const lock      = this.route.snapshot.queryParams['lock'] === '1';
        const match = preselect && types.find(t => t.id === preselect);
        if (match) {
          this.leaveForm.patchValue({ leaveTypeId: match.id });
          this.leaveTypeSearchCtrl.setValue(match.name, { emitEvent: false });
          if (lock) {
            this.leaveForm.get('leaveTypeId')?.disable();
            this.leaveTypeSearchCtrl.disable();
          }
        }
      });

    this.leaveService
      .getManagers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mgrs) => {
        done();
        this.managers = mgrs;
        // Auto-select single supervisor per PBI: "In case of single only one supervisor
        // name will be displayed with no option to edit it"
        if (mgrs.length === 1) {
          this.selectedLeaveManager   = mgrs[0];
          this.selectedCompOffManagers = [mgrs[0]];
          this.leaveForm.patchValue({ requestToId: mgrs[0].id });
          this.compOffForm.patchValue({ requestToIds: [mgrs[0].id] });
        }
      });

    this.leaveService
      .getHolidays()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((holidays) => {
        done();
        const rows = holidays.map((h) => {
          const localDate = parseLocalDate(h.holidayDate);
          return {
            holidayName: h.holidayName,
            date: formatDateLong(localDate),
            day: getDayName(localDate),
            type: h.isOptional ? 'Optional' : 'Mandatory',
            isMandatory: !h.isOptional,
          };
        });
        this.companyHolidayRows = rows;
        this.optionalHolidayRows = rows.filter((r) => r.type === 'Optional');
      });

    this.leaveService
      .getWorkedDates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dates) => {
        done();
        this.workedDates = dates;
        this.pendingWorkedRows = dates.map((d) => ({
          dateWorked: this.fmt(d.date),
          requestedDate: d.requestedDate ? this.fmt(d.requestedDate) : '-',
          status: d.status || '-',
          _raw: d,
        }));
      });

    this.leaveService
      .getTakenCompOffs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((c) => { done(); this.takenCompOffs = c; });

    this.leaveService
      .getMyLeaveBalance()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((balances) => {
        done();
        this.leaveBalances = balances;
      });

    this.leaveService
      .getPolicyDocuments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((docs: any[]) => {
        done();
        this.policyDocuments = docs;
        console.log('[RequestLeavePage] policyDocuments loaded:', docs.map(d => ({ leaveTypeId: d.leaveTypeId, title: d.documentTitle, name: d.name, url: d.documentUrl?.substring(0, 60) })));
      });

  }

  // ─── Tab Switching ────────────────────────────────────────────────────────

  switchLeftTab(tab: number): void {
    this.leftTab = tab;
    this.rightTab = tab === 1 ? 2 : 0;
  }

  // ─── Leave Request Manager Selection ─────────────────────────────────────

  onLeaveManagerChange(id: string): void {
    this.selectedLeaveManager = this.managers.find((m) => m.id === id) || null;
  }

  removeLeaveManager(): void {
    this.selectedLeaveManager = null;
    this.leaveForm.patchValue({ requestToId: '' });
  }

  // ─── Leave-type autocomplete ──────────────────────────────────────────────

  onLeaveTypeSelected(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value as string;
    const found = this.leaveTypes.find(t => t.id === id);
    if (found) {
      this.leaveForm.patchValue({ leaveTypeId: id });
      this.leaveTypeSearchCtrl.setValue(found.name, { emitEvent: false });
    }
    const docs = this.policyDocuments.filter((d: any) => d.leaveTypeId === id);
    console.log('[RequestLeavePage] leaveType selected:', id, 'matched docs:', docs.map(d => ({ leaveTypeId: d.leaveTypeId, title: d.documentTitle })));
    this.policyLinks = docs.map((d: any) => ({
      url: d.documentUrl,
      name: d.documentTitle ?? d.name ?? this.extractFileName(d.documentUrl) ?? 'Leave Document',
    }));
    // Keep legacy single-doc props for backward compat
    this.policyLinkUrl = docs[0]?.documentUrl ?? null;
    this.policyLinkName = docs[0]?.documentTitle ?? docs[0]?.name ?? this.extractFileName(docs[0]?.documentUrl) ?? null;
  }

  toggleLeaveTypePanel(event: Event): void {
    event.stopPropagation();
    if (this.leaveTypeAutoTrigger?.panelOpen) {
      this.leaveTypeAutoTrigger.closePanel();
    } else {
      this.leaveTypeAutoTrigger?.openPanel();
    }
  }

  openPolicyLink(): void {
    if (this.policyLinkUrl) this._openDocPreview(this.policyLinkUrl);
  }

  openPolicyDoc(url: string): void {
    if (url) this._openDocPreview(url);
  }

  private _openDocPreview(url: string): void {
    const fileName = this.extractFileName(url) ?? 'Document';
    this.leaveService.downloadProxiedDocument(url).subscribe({
      next: (blob) => {
        this.dialog.open<DocPreviewDialogComponent, DocPreviewDialogData>(
          DocPreviewDialogComponent,
          { data: { src: blob, fileName }, maxWidth: '95vw', panelClass: 'lm-dialog-panel' }
        );
      },
      error: () => window.open(url, '_blank', 'noopener,noreferrer'),
    });
  }

  private extractFileName(url: string | null | undefined): string | null {
    if (!url) return null;
    const decoded = decodeURIComponent(url);
    const parts = decoded.split('/');
    return parts[parts.length - 1] || null;
  }

  // ─── Comp Off Manager Selection (supports multiple) ───────────────────────

  onCompOffManagersChange(ids: string[]): void {
    this.selectedCompOffManagers = this.managers.filter(m => ids.includes(m.id));
  }

  removeCompOffManager(id: string): void {
    this.selectedCompOffManagers = this.selectedCompOffManagers.filter(m => m.id !== id);
    const current: string[] = this.compOffForm.get('requestToIds')?.value ?? [];
    this.compOffForm.patchValue({ requestToIds: current.filter(i => i !== id) });
  }

  // ─── Comp Off Date Chips (date picker → chip list) ───────────────────────

  onCompOffDateClosed(): void {
    const d = this.compOffDateCtrl.value;
    if (!d) return;
    const alreadyAdded = this.selectedCompOffDates.some(
      (x) => x.toDateString() === d.toDateString()
    );
    if (!alreadyAdded) {
      this.selectedCompOffDates = [...this.selectedCompOffDates, d];
      this.compOffForm.patchValue({ requestedDays: this.selectedCompOffDates.length });
    }
    setTimeout(() => this.compOffDateCtrl.setValue(null), 0);
  }

  removeCompOffDate(date: Date): void {
    this.selectedCompOffDates = this.selectedCompOffDates.filter(
      (d) => d.toDateString() !== date.toDateString()
    );
    this.compOffForm.patchValue({
      requestedDays: this.selectedCompOffDates.length || 1,
    });
  }

  // ─── Worked Date Chips (date picker → chip list) ─────────────────────────

  onWorkedDateClosed(): void {
    const d = this.workedDateCtrl.value;
    if (!d) return;
    const alreadyAdded = this.selectedWorkedDates.some(
      (x) => x.toDateString() === d.toDateString()
    );
    if (!alreadyAdded) {
      this.selectedWorkedDates = [...this.selectedWorkedDates, d];
    }
    setTimeout(() => this.workedDateCtrl.setValue(null), 0);
  }

  removeWorkedDate(date: Date): void {
    this.selectedWorkedDates = this.selectedWorkedDates.filter(
      (d) => d.toDateString() !== date.toDateString()
    );
  }

  // ─── Swap Comp Off Dates ⇄ Worked Dates ─────────────────────────────────

  swapDates(): void {
    if (this.selectedCompOffDates.length !== this.selectedWorkedDates.length) return;
    const workedCopy  = [...this.selectedWorkedDates];
    const compOffCopy = [...this.selectedCompOffDates];
    this.selectedCompOffDates = workedCopy;
    this.selectedWorkedDates  = compOffCopy;
    this.compOffForm.patchValue({ requestedDays: workedCopy.length || 1 });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  /** MM/DD/YYYY — used for right-panel grid display */
  fmt(d: string | Date): string {
    return formatDateMDY(d);
  }

  /** MM/DD/YYYY — per PBI spec for date chip labels */
  fmtMDY(d: Date): string {
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  formatLocalDate(date: any): string {
    // Use extractDateString to handle Moment objects from MomentDateAdapter (useUtc: true)
    // as well as native Date objects, avoiding UTC timezone shift.
    return extractDateString(date) || '';
  }

  formatTakenWorkedRange(tc: TakenCompOff): string {
    const from = this.fmt(tc.workedDateFrom);
    const to = tc.workedDateTo ? this.fmt(tc.workedDateTo) : null;
    return to && to !== from ? `${from} - ${to}` : from;
  }

  formatTakenRequestedRange(tc: TakenCompOff): string {
    const from = this.fmt(tc.requestedDateFrom);
    const to = tc.requestedDateTo ? this.fmt(tc.requestedDateTo) : null;
    return to && to !== from ? `${from} - ${to}` : from;
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  onReset(): void {
    if (this.leftTab === 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.leaveForm.reset({
        requestToId: '',
        leaveTypeId: this.leaveTypes[0]?.id ?? '',
        startDate: today,
        endDate: today,
        description: '',
      });
      this.selectedLeaveManager = null;
    } else {
      this.compOffForm.reset({ requestedDays: 1, requestToIds: [] });
      this.selectedCompOffDates    = [];
      this.selectedWorkedDates     = [];
      this.selectedCompOffManagers = this.managers.length === 1 ? [this.managers[0]] : [];
      this.compOffDateCtrl.setValue(null);
      this.workedDateCtrl.setValue(null);
      if (this.managers.length === 1) {
        this.compOffForm.patchValue({ requestToIds: [this.managers[0].id] });
      }
    }
  }

  private showSuccess(title?: string, desc?: string): void {
    const ref = this.dialog.open(SuccessDialogComponent, {
      width: '820px',
      maxWidth: '95vw',
      data: { title, desc },
    });
    ref.afterClosed().subscribe(() => this.navigateBack());
  }

  private showError(message: string): void {
    this.notify.addError(message);
  }

  onSubmit(): void {
    if (this.leftTab === 0) {
      if (this.leaveForm.invalid) {
        this.leaveForm.markAllAsTouched();
        return;
      }
      this.isSubmitting = true;
      const v = this.leaveForm.getRawValue();
      this.leaveService
        .createLeaveRequest({
          leaveTypeId: v.leaveTypeId,
          leaveStartDate: this.formatLocalDate(new Date(v.startDate)),
          leaveEndDate: this.formatLocalDate(new Date(v.endDate)),
          requestReason: v.description || undefined,
          isHourlyLeaveRequest: false,
          requestedToEmployeeId: v.requestToId || undefined,
        })
        .subscribe({
          next: (res) => {
            this.isSubmitting = false;
            if (res?.success === false || (res as any)?.error) {
              this.showError((res as any)?.error ?? 'Failed to submit leave request.');
            } else {
              this.showSuccess();
            }
          },
          error: () => { this.isSubmitting = false; },
        });
    } else {
      if (
        this.compOffForm.invalid ||
        this.selectedCompOffDates.length === 0 ||
        this.selectedWorkedDates.length === 0
      ) {
        this.compOffForm.markAllAsTouched();
        return;
      }

      // Each comp-off date must be paired with exactly one worked date
      if (this.selectedCompOffDates.length !== this.selectedWorkedDates.length) {
        this.showError(
          `Number of Comp-Off dates (${this.selectedCompOffDates.length}) must match ` +
          `number of Worked dates (${this.selectedWorkedDates.length}).`
        );
        return;
      }

      const requestToId = this.selectedCompOffManagers[0]?.id;
      const pairs = this.selectedCompOffDates.map((dateOff, i) => ({
        compOffRequestedForDate:     this.formatLocalDate(dateOff),
        compOffRequestedAgainstDate: this.formatLocalDate(this.selectedWorkedDates[i]),
        ...(requestToId ? { requestedToEmployeeId: requestToId } : {}),
      }));

      if (pairs.length === 0) return;

      this.isSubmitting = true;
      forkJoin(pairs.map(dto => this.leaveService.createCompOffRequest(dto)))
        .subscribe(results => {
          this.isSubmitting = false;
          const failed = results.filter((r: any) => r?.success === false || (r as any)?.error);
          if (failed.length > 0) {
            const msg = (failed[0] as any)?.error ?? 'One or more comp-off requests failed.';
            this.showError(msg);
          } else {
            this.showSuccess(
              'Comp-Off Request Submitted!',
              'Your comp-off request has been sent for approval.',
            );
          }
        });
    }
  }

  navigateBack(): void {
    this.router.navigate(['/ers/leave-management']);
  }
}
